import { DKDialog } from "../../../common/DKDialog";
import React, { useEffect, useState } from "react";
import { UploadDealDocumentFilePicker } from "../UploadDealDocumentFilePicker";
import * as api from "../../../../api/generated-api";
import { DealDocumentType } from "../../../../api/generated-api";
import { DealPageActionButtonInfo } from "../../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import { isEmpty } from "../../../../helpers/utilsTS";
import DKFormTextInput from "../../../common/widgets/formVariablesWidget/DKFormTextInput";
import { filenameWithoutExtension } from "../../../../api/deals/documents";
import Button from "../../../common/forms/button";
import { Box, Stack, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DKFormSelect from "../../../common/forms/DKFormSelect";

type UploadDocumentModalProps = {
  open: boolean;
  dataTestId: string;
  nonChangeableDocumentType?: DealDocumentType;
  documentTypes: DealDocumentType[];
  onCancel: () => void;
  onDocumentSave: (
    event: React.ChangeEvent<HTMLInputElement>,
    docType: api.DealDocumentType,
    docName: string
  ) => Promise<api.DealDocument>;
};
export const UploadDocumentModal = ({
  open,
  dataTestId,
  nonChangeableDocumentType,
  documentTypes,
  onCancel,
  onDocumentSave,
}: UploadDocumentModalProps) => {
  const [filePickerOpen, setFilePickerOpen] = useState<boolean>(false);
  const [fileChosenEvent, setFileChoseEvent] =
    useState<React.ChangeEvent<HTMLInputElement>>();
  const [fileSystemFileName, setFileSystemFileName] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [documentType, setDocumentType] = useState<DealDocumentType>(
    nonChangeableDocumentType ? nonChangeableDocumentType : documentTypes[0]
  );
  const [fileChosen, setFileChosen] = useState<boolean>(false);

  const isReadyToSave = () => {
    return !isEmpty(fileName) && fileChosen && fileChosenEvent;
  };

  useEffect(() => {
    if (nonChangeableDocumentType) {
      setDocumentType(nonChangeableDocumentType);
    }
  }, [nonChangeableDocumentType]);

  const resetState = () => {
    //The value of the target in this case is that of the input field for the file
    //If you do not set the value back to null, then subsequent onChange events will not fire
    //I would like to do this closer to the <input> but the event gets used several components up the stack
    if (fileChosenEvent?.target) {
      fileChosenEvent.target.value = null;
    }
    setFileChoseEvent(undefined);
    setFileSystemFileName("");
    setFileName("");
    setFileChosen(false);
    setFilePickerOpen(false);
    setDocumentType(
      nonChangeableDocumentType ? nonChangeableDocumentType : documentTypes[0]
    );
  };

  const actionButtons: DealPageActionButtonInfo[] = [];
  actionButtons.push({
    dataTestId: "cancel-upload-dialog-button",
    onClick: () => {
      resetState();
      onCancel();
    },
    primary: false,
    label: "Cancel",
  });

  actionButtons.push({
    dataTestId: "save-upload-dialog-button",
    disabled: !isReadyToSave(),
    onClick: async () => {
      await onDocumentSave(fileChosenEvent, documentType, fileName);
      resetState();
    },
    primary: true,
    label: "Upload",
  });

  return (
    <DKDialog
      title={"Add Document"}
      open={open}
      contentText={""}
      actionButtons={actionButtons}
    >
      <Box mb={2} display={"flex"} flexDirection={"row"} alignItems={"center"}>
        {!fileChosen && (
          <Button
            disabled={fileChosen}
            data-testid={"upload-upload-dialog-button"}
            type={"primary"}
            size="large"
            onClick={() => {
              setFilePickerOpen(true);
            }}
          >
            Choose File
          </Button>
        )}
        {fileChosen && (
          <>
            <Typography justifyContent={"center"} variant={"body2"}>
              {fileSystemFileName}
            </Typography>
            <ClearIcon
              sx={{ cursor: "pointer", marginLeft: 2 }}
              onClick={resetState}
            />
          </>
        )}
      </Box>
      <UploadDealDocumentFilePicker
        visible={filePickerOpen}
        dataTestId={dataTestId}
        documentType={documentType}
        onDocumentSelect={async (event) => {
          setFileChoseEvent(event);
          return null;
        }}
        onDocumentSelected={(event) => {
          setFileChosen(true);
          setFilePickerOpen(false);
          setFileSystemFileName(event.target.files[0].name);
          setFileName(filenameWithoutExtension(event.target.files[0].name));
        }}
        onCancel={() => {
          setFileChosen(false);
          setFilePickerOpen(false);
        }}
      />
      {fileChosen && (
        <Stack>
          <DKFormTextInput
            name={"uploadingFileName"}
            label={fileChosen ? "Document Name" : "Choose File To Set Name"}
            disabled={!fileChosen}
            value={fileName}
            dataTestId={"file-name-upload-dialog-field"}
            onChange={(event) => {
              setFileName(event.target.value);
            }}
          />
          {!nonChangeableDocumentType && (
            <DKFormSelect
              label={"Document Type"}
              dataTestId={"document-type-upload-dialog-field"}
              selectedValue={documentType}
              onChange={(event) => {
                setDocumentType(event.target.value as DealDocumentType);
              }}
              items={documentTypes.map((docType) => {
                return { value: docType, label: docType };
              })}
            />
          )}
        </Stack>
      )}
    </DKDialog>
  );
};
